import { getConfig } from '@edx/frontend-platform';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import UnitTitlePlugin, { TranslationsLoader, TranslationsError } from '@edx/unit-title-plugin';
import { DatadogLoggingService } from '@edx/frontend-logging';
import { ErrorBoundary } from '@edx/frontend-platform/react';
import {
  CourseExitUpsell,
  LockPaywall,
  UpgradeNotification,
  NextUnitButton,
  SectionList,
  AccessExpirationAlert,
  DynamicExperienceSlot,
  UclWrapper,
  UpgradeToCompleteAlert,
} from '@edx/frontend-plugin-advertisements';
import { PLUGIN_OPERATIONS, DIRECT_PLUGIN } from '@openedx/frontend-plugin-framework';
import OutlinePlugin from '@edx/left-sidebar-navigation-plugin';
import CourseOutlineSidebarTriggerPlugin from '@edx/course-outline-sidebar-trigger-plugin';
import NotificationsTray from '@edx/frontend-plugin-notifications';
import {
  hideCourseSuggestionsForEnterpriseLearners,
  modifyViewCoursesForEnterpriseLearners,
  modifyDashboardFootnoteForEnterpriseLearners,
} from '@edx/course-end-enterprise-plugins';
import CohesionWrapper from "@edx/cohesion-wrapper";
import { CourseGrade, ProgressTabGradeBreakdownPlugin } from '@edx/progress-tab-grade-plugins';
import { XpertChatWidget } from '@edx/frontend-lib-learning-assistant';
import { TrackSelectionPage } from '@edx/track-selection-plugin';

const modifyProgressCertificateWidget = (widget) => {
  const { RenderWidget } = widget;
  if (RenderWidget.props.id.includes('upgrade')) {
    widget.RenderWidget = UpgradeNotification({ id: 'progress_certificate_status_slot' });
  }

  return widget;
};

// LP-1419: DISABLE_RED_VENTURES_WIDGETS (APP_CONFIG in the *_config.yml) controls the Red Ventures
// dynamic-experience widgets in the notification tray, course outline notifications and dates banner.
// Unset or anything other than "true" (default): Red Ventures widgets. "true": render the in-house components.
// ucl_plugin is not affected, since the Next Unit upgrade modal depends on the Red Ventures script.
const withRedVenturesSwitch = (InhouseComponent, placement) => (pluginProps) => {
  if (String(getConfig().DISABLE_RED_VENTURES_WIDGETS).toLowerCase() === 'true') {
    return <InhouseComponent {...pluginProps} />;
  }
  return (
    <ErrorBoundary fallbackComponent={<InhouseComponent {...pluginProps} />}>
      <DynamicExperienceSlot
        widgetName='MonarchResolvedDynamicExperience'
        placement={placement}
        tenantId='2u-edx-learning'
        pluginProps={pluginProps}
      />
    </ErrorBoundary>
  );
};

// Load environment variables from .env file
const config = {
  ...process.env,
  AI_TRANSLATIONS_URL: 'https://ai-translations.stage.edx.org',
  loggingService: DatadogLoggingService,
  OPTIMIZELY_FULL_STACK_SDK_KEY: 'GiXcfTudkzWv8T29KJgES',
  FEATURE_ENABLE_CHAT_V2_ENDPOINT: 'true',
  DISABLE_RED_VENTURES_WIDGETS: process.env.DISABLE_RED_VENTURES_WIDGETS,
  // LP-1418: TRACK_SELECTION_ODYSSEY (APP_CONFIG in the *_config.yml) "true" shows the redesigned track
  // selection page. A boolean here, because the plugin treats any non-empty value, "false" too, as on.
  TRACK_SELECTION_ODYSSEY: String(process.env.TRACK_SELECTION_ODYSSEY).toLowerCase() === 'true',
  pluginSlots: {
    unit_title_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'unit_title_widget',
            type: DIRECT_PLUGIN,
            RenderWidget: (props) => {
              const dispatch = useDispatch();
              return <UnitTitlePlugin {...props} dispatch={dispatch} />;
            },
          },
        },
      ],
    },
    sequence_container_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: "sequence_container_widget",
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: () => <div id="whole-course-translation-feedback-widget" />,
          },
        },
      ],
    },
    gated_unit_content_message_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'gated_unit_content_message_slot',
            type: DIRECT_PLUGIN,
            priority: 50,
            RenderWidget: LockPaywall,
          },
        },
      ],
    },
    notification_widget_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'notification_widget_slot',
            type: DIRECT_PLUGIN,
            priority: 50,
            RenderWidget: UpgradeNotification,
          },
        },
      ],
    },
    'org.openedx.frontend.learning.notification_tray.v1': {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'notification_tray_slot',
            type: DIRECT_PLUGIN,
            priority: 50,
            errorFallbackComponent: UpgradeNotification,
            RenderWidget: withRedVenturesSwitch(UpgradeNotification, 'org.openedx.frontend.learning.notification_tray.v1'),
          },
        },
      ],
    },
    'org.openedx.frontend.learning.course_outline_tab_notifications.v1': {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'outline_tab_notifications_slot',
            type: DIRECT_PLUGIN,
            priority: 50,
            errorFallbackComponent: UpgradeNotification,
            RenderWidget: withRedVenturesSwitch(UpgradeNotification, 'org.openedx.frontend.learning.course_outline_tab_notifications.v1'),
          },
        },
      ],
    },
    next_unit_top_nav_trigger_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'next_unit_top_nav_trigger_slot',
            type: DIRECT_PLUGIN,
            priority: 50,
            RenderWidget: NextUnitButton,
          },
        },
      ],
    },
    content_iframe_loader_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: "content_iframe_loader_slot",
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: TranslationsLoader,
          },
        },
      ],
    },
    progress_certificate_status_slot: {
      keepDefault: true,
      plugins: [{
        op: PLUGIN_OPERATIONS.Modify,
        widgetId: 'default_contents',
        fn: modifyProgressCertificateWidget,
      }]
    },
    course_outline_sidebar_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'custom_sidebar_component',
            type: DIRECT_PLUGIN,
            RenderWidget: OutlinePlugin,
          },
        },
      ]
    },
    course_outline_sidebar_trigger_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'course_outline_sidebar_trigger_slot',
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: (props) => {
              return (
                <CourseOutlineSidebarTriggerPlugin Link={Link} {...props} />
              );
            },
          },
        },
      ]
    },
    notifications_tray: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'notification_tray',
            type: DIRECT_PLUGIN,
            RenderWidget: () => <NotificationsTray margins="user-header-secondary-item" />,
          },
        }
      ]
    },
    'org.openedx.frontend.learning.course_recommendations.v1': {
      keepDefault: true,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Modify,
          widgetId: 'default_contents',
          fn: hideCourseSuggestionsForEnterpriseLearners,
        },
      ]
    },
    course_home_section_outline_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'custom_section_outline_component',
            type: DIRECT_PLUGIN,
            RenderWidget: SectionList,
          },
        },
      ]
    },
    'org.openedx.frontend.learning.course_exit_view_courses.v1': {
      keepDefault: true,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Modify,
          widgetId: 'default_contents',
          fn: modifyViewCoursesForEnterpriseLearners,
        },
      ],
    },
    'org.openedx.frontend.learning.course_exit_dashboard_footnote_link.v1': {
      keepDefault: true,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Modify,
          widgetId: 'default_contents',
          fn: modifyDashboardFootnoteForEnterpriseLearners,
        },
      ],
    },
    header_slot: {
      keepDefault: true,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'cohesion_plugin',
            type: DIRECT_PLUGIN,
            priority: 70,
            RenderWidget: CohesionWrapper,
          },
        },
      ]
    },
    'org.openedx.frontend.learning.content_iframe_error.v1': {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: "iframe_error",
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: TranslationsError
          },
        },
      ],
    },
    progress_tab_course_grade_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'progress_tab_course_grade_slot',
            type: DIRECT_PLUGIN,
            RenderWidget: CourseGrade,
          },
        },
      ],
    },
    progress_tab_grade_breakdown_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'progress_tab_grade_breakdown_slot',
            type: DIRECT_PLUGIN,
            RenderWidget: ProgressTabGradeBreakdownPlugin,
          },
        },
      ],
    },
    learner_tools_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'xpert_learning_assistant',
            type: DIRECT_PLUGIN,
            priority: 50,
            RenderWidget: XpertChatWidget,
          },
        },
      ],
    },
    courseware_top_banner_slot: {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'courseware_top_access_expiration_alert',
            type: DIRECT_PLUGIN,
            priority: 50,
            RenderWidget: AccessExpirationAlert,
          },
        },
      ],
    },
    'org.openedx.frontend-app-learning.course-exit-upsell.v1': {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'course_exit_upsell_widget',
            type: DIRECT_PLUGIN,
            priority: 1,
            RenderWidget: CourseExitUpsell,
          },
        },
      ],
    },
    'org.openedx.frontend.layout.footer.v1': {
      keepDefault: true,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'ucl_plugin',
            type: DIRECT_PLUGIN,
            priority: 80,
            RenderWidget: UclWrapper,
          },
        },
      ],
    },
    'org.openedx.frontend.learning.banner_dates_upgrade.v1': {
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'dates_upgrade_banner_slot',
            type: DIRECT_PLUGIN,
            priority: 50,
            RenderWidget: withRedVenturesSwitch(UpgradeToCompleteAlert, 'org.openedx.frontend.learning.banner_dates_upgrade.v1'),
          },
        },
      ],
    },
    track_selection_slot: {
      keepDefault: false,
      plugins: [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'track_selection_page',
            type: DIRECT_PLUGIN,
            RenderWidget: TrackSelectionPage,
          },
        },
      ],
    },
  },
};

export default config;
